import { AppSettings } from '@/AppSettings';
import { crfReadOnly } from '@/utils/CrfFormUtils';
import { LookupYesNoType, PatientGetSingleById, RandomisationForm as RandomisationFormDto } from '@/api/dtos';
import { FOUR_DIGIT_POSITIVE_INTEGER } from '@/utils/formatting/NumericFormats';
import { Box } from '@mui/material';
import { ConditionMode, ValidateOn } from '@ngt/forms-core';
import {
    Autocomplete,
    Checkbox,
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    FormBreadcrumbs,
    FormDefinitionContext,
    GroupedField,
    GroupedFields,
    ICrfConditionParameters,
    InputOptionMode,
    InstitutionContext,
    MasterGroupContext,
    Numeric,
    OnlinePatientManagementContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const mainLanguageChildOther = ({ formState }: ICrfConditionParameters<RandomisationFormDto>) =>
    formState?.value?.mainLanguageChildOther === true;

const mainLanguageChildNotOther = ({ formState }: ICrfConditionParameters<RandomisationFormDto>) =>
    formState?.value?.mainLanguageChildOther !== true;

const mainLanguageOther = ({ formState }: ICrfConditionParameters<RandomisationFormDto>) =>
    formState?.value?.mainLanguageOther === true;

const mainLanguageNotOther = ({ formState }: ICrfConditionParameters<RandomisationFormDto>) =>
    formState?.value?.mainLanguageOther !== true;

const birthCountryOther = ({ formState }: ICrfConditionParameters<RandomisationFormDto>) =>
    formState?.value?.birthCountryOther === true;

const birthCountryNotOther = ({ formState }: ICrfConditionParameters<RandomisationFormDto>) =>
    formState?.value?.birthCountryOther !== true;

const ethnicityKnown = ({ formState }: ICrfConditionParameters<RandomisationFormDto>) =>
    formState?.value?.ethnicityUnkown !== true;

const isIndigenous = ({ formState }: ICrfConditionParameters<RandomisationFormDto>) =>
    formState?.value?.indigenous === LookupYesNoType.Yes;

const LabeledCheckboxField: FC<{ propertyPath: string }> = ({ propertyPath }) => (
    <GroupedField
        propertyPath={propertyPath}
        component={Checkbox}
        LabelProps={{ label: <></> }}
        formInputOptions={{ labelMode: InputOptionMode.Auto }}
    />
);

const RandomisationFields: FC = () => (
    <>
        <GroupedField propertyPath="randomisationArm" component={Select} />

        <GroupedField 
            propertyPath="randNo" 
            component={Text} 
            placeholder="XXX-XXX"
        />

        <GroupedField 
            propertyPath="postCode" 
            component={Numeric} 
            numberFormat={FOUR_DIGIT_POSITIVE_INTEGER}
        />

        <GroupedFields>
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={mainLanguageChildNotOther}
                subscription={{ value: true }}
            >
                <GroupedField propertyPath="mainLanguageChild" component={Autocomplete} />
            </CrfCondition>
            <LabeledCheckboxField propertyPath="mainLanguageChildOther" />
            <CrfCondition
                mode={ConditionMode.Show}
                condition={mainLanguageChildOther}
                subscription={{ value: true }}
            >
                <GroupedField propertyPath="mainLanguageChildOtherSpecify" component={Text} />
            </CrfCondition>
        </GroupedFields>

        <GroupedFields>
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={mainLanguageNotOther}
                subscription={{ value: true }}
            >
                <GroupedField propertyPath="mainLanguage" component={Autocomplete} />
            </CrfCondition>
            <LabeledCheckboxField propertyPath="mainLanguageOther" />
            <CrfCondition
                mode={ConditionMode.Show}
                condition={mainLanguageOther}
                subscription={{ value: true }}
            >
                <GroupedField propertyPath="mainLanguageOtherSpecify" component={Text} />
            </CrfCondition>
        </GroupedFields>

        <GroupedFields>
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={birthCountryNotOther}
                subscription={{ value: true }}
            >
                <GroupedField propertyPath="birthCountry" component={Autocomplete} />
            </CrfCondition>
            <LabeledCheckboxField propertyPath="birthCountryOther" />
            <CrfCondition
                mode={ConditionMode.Show}
                condition={birthCountryOther}
                subscription={{ value: true }}
            >
                <GroupedField propertyPath="birthCountryOtherSpecify" component={Text} />
            </CrfCondition>
        </GroupedFields>

        <GroupedFields>
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={ethnicityKnown}
                subscription={{ value: true }}
            >
                <GroupedField propertyPath="ethnicity" component={Autocomplete} />
            </CrfCondition>
            <LabeledCheckboxField propertyPath="ethnicityUnkown" />
        </GroupedFields>

        <GroupedFields>
            <GroupedField propertyPath="indigenous" component={Select} />
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={isIndigenous}
                subscription={{ value: true }}
            >
                <LabeledCheckboxField propertyPath="indigenousAboriginal" />
                <LabeledCheckboxField propertyPath="indigenousSouthSeaIslander" />
                <LabeledCheckboxField propertyPath="indigenousTorresStraitIslander" />
                <LabeledCheckboxField propertyPath="indigenousNorfolkIslander" />
                <LabeledCheckboxField propertyPath="indigenousMaori" />
                <LabeledCheckboxField propertyPath="indigenousPacificPeoples" />
            </CrfCondition>
        </GroupedFields>
    </>
);

const RandomisationForm: FC = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    const { data: formDefinition } = useContext(FormDefinitionContext);
    const { routeParameters, serviceStackClient } = useContext(OnlinePatientManagementContext);
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const refreshPatientAndNavigate = useCallback(async (returnToSummary: boolean) => {
        if (!patient?.id) {
            enqueueSnackbar('Unable to reload the participant because their ID is unavailable.', { variant: 'error' });
            return;
        }

        const api = await serviceStackClient.api(new PatientGetSingleById({ id: patient.id }));
        const updatedStudyNumber = api.response?.patient?.studyNumber;

        if (!api.succeeded || !updatedStudyNumber) {
            enqueueSnackbar(api.error?.message ?? 'Unable to reload the participant after saving.', { variant: 'error' });
            return;
        }

        const institutionCode = params[routeParameters.institutionCode];

        if (!institutionCode) {
            enqueueSnackbar('Unable to update the participant URL because the institution code is unavailable.', { variant: 'error' });
            return;
        }

        const patientPath = `/registration/${encodeURIComponent(institutionCode)}/${encodeURIComponent(updatedStudyNumber)}`;

        if (returnToSummary) {
            navigate(patientPath, { replace: true });
            return;
        }

        const eventDefinitionCode = params[routeParameters.eventDefinitionCode];
        const eventRepeat = params[routeParameters.eventRepeat];
        const formDefinitionCode = params[routeParameters.formDefinitionCode];
        const formRepeat = params[routeParameters.formRepeat];

        if (!eventDefinitionCode || !eventRepeat || !formDefinitionCode || !formRepeat) {
            enqueueSnackbar('Unable to update the form URL after saving.', { variant: 'error' });
            return;
        }

        navigate({
            pathname: `${patientPath}/${encodeURIComponent(eventDefinitionCode)}/${encodeURIComponent(eventRepeat)}/${encodeURIComponent(formDefinitionCode)}/${encodeURIComponent(formRepeat)}`,
            search: location.search,
            hash: location.hash,
        }, { replace: true });
    }, [enqueueSnackbar, location.hash, location.search, navigate, params, patient?.id, routeParameters, serviceStackClient]);

    const {
        permissions: [canUpdateCrf],
        loading: permissionsLoading,
    } = usePermissionsByIds(
        [Permission.OpmsCrfUpdate],
        masterGroup?.id,
        collaboratingGroup?.id,
        institution?.id,
        patient?.id
    );

    if (permissionsLoading) {
        return <RouteLoading />;
    }

    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                debug={AppSettings.debug}
                afterFormSave={() => void refreshPatientAndNavigate(false)}
                afterFormSaveAndReturn={() => void refreshPatientAndNavigate(true)}
                AdditionalContent={{
                    beforeChildren: formDefinition?.instructions ?
                        <Box
                            sx={{
                                p: 2,
                            }}
                        >
                            {formDefinition?.instructions}
                        </Box> :
                        undefined
                }}
            >
                <RandomisationFields />
            </CrfForm>
        </PageLayout>
    );
};

export default RandomisationForm;
